<template>
	<div class="graphEditorRoot" role="dialog" aria-labelledby="graphEditorTitle">
		<div id="graphEditorTitle" class="title">{{isNew ? "Add Graph" : "Edit Graph"}}</div>
		<div class="editorBody">
			<div class="controlsPane">
				<div class="fieldBlock">
					<label class="fieldLabel" for="graphTitleInput">Graph Title</label>
					<input id="graphTitleInput" type="text" class="wideInput" v-model="model.title" placeholder="e.g. Active Session Count - Last 24 Hours" />
					<div v-if="isTemplate" class="fieldHelp">Tip: include <code class="inline">%%ServerName%%</code> in the title to show which server the graph belongs to.</div>
				</div>

				<div class="fieldBlock">
					<div class="fieldLabel">Time Range</div>
					<div class="timeRangeRow">
						<select v-model="fromPreset" aria-label="Time range preset">
							<option v-for="p in timeRangePresets" :key="p.value" :value="p.value">{{p.label}} ({{p.value}})</option>
							<option value="__custom__">Custom…</option>
						</select>
						<input v-if="fromPreset === '__custom__'" type="text" v-model="model.from" placeholder="-8h" aria-label="Custom time range start" />
					</div>
					<div class="fieldHelp">{{timeRangeHelp}}</div>
					<details class="advancedTime">
						<summary>End time (advanced)</summary>
						<input type="text" v-model="model.until" placeholder="empty = now" aria-label="Time range end" />
						<div class="fieldHelp">Where the graph ends. Leave empty to end at the current time. Accepts the same syntax as the start of the time range, e.g. <code class="inline">-1h</code> ends the graph one hour ago.</div>
					</details>
				</div>

				<div class="fieldBlock">
					<div class="fieldLabel sectionHead">Series <span class="labelNote">({{model.targets.length}})</span></div>
					<div v-if="!model.targets.length" class="fieldHelp">This graph has no series yet. Add one to begin.</div>
					<div v-for="(target, index) in model.targets" :key="targetUids[index]" class="targetBox">
						<div class="targetHeader">
							<a role="button" tabindex="0" class="targetToggle"
							   :aria-expanded="expandedTarget === index ? 'true' : 'false'"
							   @click="toggleTarget(index)"
							   @keydown.enter.space.prevent="toggleTarget(index)">
								<span aria-hidden="true">{{expandedTarget === index ? "▼" : "▶"}}</span>
								Series {{index + 1}}
							</a>
							<span class="targetSummary" :title="target.raw">{{target.raw || "(empty)"}}</span>
							<span v-if="target.parseError" class="targetError" title="This series has a syntax error">⚠</span>
							<span class="targetButtons">
								<input type="button" value="▲" title="Move up" :disabled="index === 0" @click="moveTarget(index, -1)" />
								<input type="button" value="▼" title="Move down" :disabled="index === model.targets.length - 1" @click="moveTarget(index, 1)" />
								<input type="button" value="⧉" title="Duplicate this series" @click="duplicateTarget(index)" />
								<input type="button" value="✕" title="Remove this series" @click="removeTarget(index)" />
							</span>
						</div>
						<div v-if="expandedTarget === index" class="targetBody">
							<GraphiteTargetEditor :target="target"
												  :functionsMeta="functionsMeta"
												  :templateMode="isTemplate"
												  :serverNames="serverNames" />
						</div>
					</div>
					<div class="addSeriesRow">
						<input type="button" value="+ Add Series" @click="addTarget" />
					</div>
				</div>

				<div class="fieldBlock">
					<div class="fieldLabel sectionHead">Display Options</div>
					<div v-for="opt in renderOptions" :key="opt.key" class="optionRow">
						<label class="optionLabel" :for="'graphOpt_' + opt.key + '_' + uid">{{opt.label}}</label>
						<select v-if="opt.type === 'select'"
								:id="'graphOpt_' + opt.key + '_' + uid"
								:value="model.options[opt.key] || ''"
								@change="setOption(opt.key, $event.target.value)">
							<option value="">(default)</option>
							<option v-for="c in opt.choices" :key="c" :value="c">{{c}}</option>
						</select>
						<select v-else-if="opt.type === 'bool'"
								:id="'graphOpt_' + opt.key + '_' + uid"
								:value="model.options[opt.key] || ''"
								@change="setOption(opt.key, $event.target.value)">
							<option value="">(default)</option>
							<option value="true">{{opt.trueLabel || "true"}}</option>
							<option value="false">{{opt.falseLabel || "false"}}</option>
						</select>
						<input v-else
							   :id="'graphOpt_' + opt.key + '_' + uid"
							   :type="opt.type === 'number' ? 'number' : 'text'"
							   step="any"
							   :value="model.options[opt.key] || ''"
							   @input="setOption(opt.key, $event.target.value)" />
						<a role="button" tabindex="0" class="helpToggle" :title="opt.help"
						   :aria-label="'Help for ' + opt.label"
						   @click="helpShown = helpShown === opt.key ? null : opt.key"
						   @keydown.enter.space.prevent="helpShown = helpShown === opt.key ? null : opt.key">?</a>
					</div>
					<div v-if="helpShownOption" class="optionHelpBox">
						<b>{{helpShownOption.label}}:</b> {{helpShownOption.help}}
					</div>
				</div>

				<div class="fieldBlock">
					<div class="fieldLabel sectionHead">Other Parameters <span class="labelNote">(advanced)</span></div>
					<div class="fieldHelp">Additional raw parameters passed to Graphite's render API, for anything not covered above (e.g. <code class="inline">bgcolor</code> = <code class="inline">white</code>).</div>
					<div v-for="(p, index) in model.otherParams" :key="index" class="otherParamRow">
						<input type="text" class="otherParamKey" v-model="p.key" placeholder="parameter" aria-label="Parameter name" />
						<span>=</span>
						<input type="text" class="otherParamValue" v-model="p.value" placeholder="value" aria-label="Parameter value" />
						<input type="button" value="✕" title="Remove parameter" @click="model.otherParams.splice(index, 1)" />
					</div>
					<input type="button" value="+ Add Parameter" @click="model.otherParams.push({ key: '', value: '' })" />
				</div>
			</div>

			<div class="previewPane">
				<div class="previewHeader">
					<span class="previewTitle">Live Preview</span>
					<label v-if="isTemplate && serverNames.length" class="previewServerLabel">
						Preview server:
						<select v-model="localPreviewServer">
							<option v-for="sn in serverNames" :key="sn" :value="sn">{{sn}}</option>
						</select>
					</label>
					<input type="button" value="Refresh" @click="refreshPreview" />
				</div>
				<div class="previewImageWrapper" ref="previewWrapper">
					<img v-if="previewSrc" :src="previewSrc" class="previewImage" alt="Graph preview" />
					<div v-if="previewLoading" class="previewLoading"><ScaleLoader /></div>
					<div v-if="previewError" class="previewErrorBox">
						<b>Preview failed to render.</b><br />
						The Graphite server may be unreachable, or the graph definition may be invalid.
					</div>
					<div v-if="!model.targets.length" class="previewEmptyBox">Add a series to see a preview.</div>
				</div>
				<div class="queryStringBox">
					<div class="queryStringHead">
						<span>Stored query string</span>
						<input type="button" value="Copy" @click="copyQueryString" />
					</div>
					<textarea class="queryStringText" readonly rows="3" :value="serializedQueryString" aria-label="Stored query string"></textarea>
					<div class="fieldHelp">This is what will be saved in GraphiteConfig.json. Image dimensions are added automatically by the viewing page.</div>
				</div>
			</div>
		</div>
		<div class="buttonRow">
			<input type="button" class="primaryButton" value="Apply" @click="apply" />
			<input type="button" value="Cancel" @click="cancel" />
		</div>
	</div>
</template>

<script>
	import { SerializeRenderQueryString, RENDER_OPTIONS, TIME_RANGE_PRESETS, TIME_RANGE_HELP } from 'appRoot/scripts/GraphiteQuery';
	import { BuildRenderSrc } from 'appRoot/scripts/GraphiteRender';
	import { GraphiteGetFunctions } from 'appRoot/api/GraphiteMetadata';
	import { ModalConfirmDialog } from 'appRoot/scripts/ModalDialog';
	import GraphiteTargetEditor from 'appRoot/vues/admin/controls/GraphiteTargetEditor.vue';

	export default {
		components: { GraphiteTargetEditor },
		props:
		{
			/** Working copy of the graph model (see GraphiteQuery.js ParseRenderQueryString); mutated in place while editing. */
			model: {
				type: Object,
				required: true
			},
			isNew: {
				type: Boolean,
				default: false
			},
			/** True for MultiServer template graphs which may use the %%ServerName%% placeholder. */
			isTemplate: {
				type: Boolean,
				default: false
			},
			serverNames: {
				type: Array,
				default: () => []
			},
			previewServer: {
				type: String,
				default: ""
			}
		},
		data()
		{
			return {
				uid: GetUid(),
				functionsMeta: null,
				expandedTarget: -1,
				targetUidCounter: 0,
				targetUids: [],
				fromPreset: "__custom__",
				helpShown: null,
				localPreviewServer: "",
				previewSrc: null,
				previewLoading: false,
				previewError: false,
				previewCacheBust: 0,
				previewTimeout: null,
				previewLoader: null,
				initialSerialized: null,
				renderOptions: RENDER_OPTIONS,
				timeRangePresets: TIME_RANGE_PRESETS,
				timeRangeHelp: TIME_RANGE_HELP
			};
		},
		created()
		{
			this.targetUids = this.model.targets.map(() => ++this.targetUidCounter);
			this.initialSerialized = SerializeRenderQueryString(this.model);
			let preset = TIME_RANGE_PRESETS.find(p => p.value === this.model.from);
			this.fromPreset = preset ? preset.value : "__custom__";
			this.localPreviewServer = this.previewServer || (this.serverNames.length ? this.serverNames[0] : "");
			if (this.isNew && !this.model.targets.length)
				this.addTarget();
			GraphiteGetFunctions()
				.then(meta => { this.functionsMeta = meta; })
				.catch(err => { toaster.warning("Graphite function documentation could not be loaded: " + err.message); });
		},
		mounted()
		{
			window.addEventListener("resize", this.onWindowResize);
			this.schedulePreview(1);
		},
		beforeUnmount()
		{
			window.removeEventListener("resize", this.onWindowResize);
			if (this.previewTimeout)
				clearTimeout(this.previewTimeout);
			this.abandonPreviewLoader();
		},
		computed:
		{
			serializedQueryString()
			{
				return SerializeRenderQueryString(this.model);
			},
			helpShownOption()
			{
				return this.helpShown ? RENDER_OPTIONS.find(o => o.key === this.helpShown) : null;
			}
		},
		methods:
		{
			SetFocus()
			{
				let input = this.$el.querySelector("#graphTitleInput");
				if (input)
					input.focus();
			},
			//////////////////////////////////////////
			// Targets                              //
			//////////////////////////////////////////
			toggleTarget(index)
			{
				this.expandedTarget = this.expandedTarget === index ? -1 : index;
			},
			addTarget()
			{
				this.model.targets.push({ raw: "", ast: null, parseError: null });
				this.targetUids.push(++this.targetUidCounter);
				this.expandedTarget = this.model.targets.length - 1;
			},
			removeTarget(index)
			{
				this.model.targets.splice(index, 1);
				this.targetUids.splice(index, 1);
				if (this.expandedTarget === index)
					this.expandedTarget = -1;
				else if (this.expandedTarget > index)
					this.expandedTarget--;
			},
			duplicateTarget(index)
			{
				let copy = JSON.parse(JSON.stringify(this.model.targets[index]));
				this.model.targets.splice(index + 1, 0, copy);
				this.targetUids.splice(index + 1, 0, ++this.targetUidCounter);
				if (this.expandedTarget > index)
					this.expandedTarget++;
			},
			moveTarget(index, direction)
			{
				let target = index + direction;
				if (target < 0 || target >= this.model.targets.length)
					return;
				let item = this.model.targets.splice(index, 1)[0];
				this.model.targets.splice(target, 0, item);
				let uidItem = this.targetUids.splice(index, 1)[0];
				this.targetUids.splice(target, 0, uidItem);
				if (this.expandedTarget === index)
					this.expandedTarget = target;
				else if (this.expandedTarget === target)
					this.expandedTarget = index;
			},
			//////////////////////////////////////////
			// Options                              //
			//////////////////////////////////////////
			setOption(key, value)
			{
				if (value === "" || value === null)
					delete this.model.options[key];
				else
					this.model.options[key] = value;
			},
			//////////////////////////////////////////
			// Preview                              //
			//////////////////////////////////////////
			onWindowResize()
			{
				this.schedulePreview(400);
			},
			schedulePreview(delay)
			{
				if (this.previewTimeout)
					clearTimeout(this.previewTimeout);
				this.previewTimeout = setTimeout(() => { this.renderPreview(); }, delay);
			},
			refreshPreview()
			{
				this.previewCacheBust++;
				this.renderPreview();
			},
			abandonPreviewLoader()
			{
				if (this.previewLoader)
				{
					this.previewLoader.onload = this.previewLoader.onerror = null;
					this.previewLoader = null;
				}
			},
			renderPreview()
			{
				if (!this.model.targets.length || this.model.targets.every(t => !t.raw))
				{
					this.previewSrc = null;
					this.previewLoading = false;
					this.previewError = false;
					return;
				}
				let wrapper = this.$refs.previewWrapper;
				if (!wrapper)
					return;
				let width = Math.max(300, wrapper.clientWidth - 2);
				let height = Math.max(200, wrapper.clientHeight - 2);
				let src = BuildRenderSrc(this.serializedQueryString, {
					width, height,
					serverName: this.isTemplate ? this.localPreviewServer : null,
					cacheBust: "preview" + this.previewCacheBust
				});
				this.previewLoading = true;
				this.previewError = false;
				this.abandonPreviewLoader();
				let loader = this.previewLoader = new Image();
				loader.onload = () =>
				{
					if (loader !== this.previewLoader)
						return;
					this.previewSrc = src;
					this.previewLoading = false;
				};
				loader.onerror = () =>
				{
					if (loader !== this.previewLoader)
						return;
					this.previewLoading = false;
					this.previewError = true;
				};
				loader.src = src;
			},
			copyQueryString()
			{
				navigator.clipboard.writeText(this.serializedQueryString)
					.then(() => { toaster.success("Copied to clipboard."); })
					.catch(err => { toaster.error("Copy failed: " + err); });
			},
			//////////////////////////////////////////
			// Dialog lifecycle                     //
			//////////////////////////////////////////
			apply()
			{
				let broken = this.model.targets.filter(t => t.parseError);
				if (broken.length)
				{
					toaster.error("A series has a syntax error which must be fixed (or the series removed) first.");
					return;
				}
				this.model.targets = this.model.targets.filter(t => t.raw && t.raw.trim());
				this.$emit("close", { model: this.model });
			},
			cancel()
			{
				this.$emit("close", false);
			},
			DefaultClose()
			{
				if (this.serializedQueryString !== this.initialSerialized)
				{
					ModalConfirmDialog("Close the graph editor and discard changes?", "Discard Changes?", "Discard", "Keep Editing")
						.then(result =>
						{
							if (result)
								this.cancel();
						});
				}
				else
					this.cancel();
			}
		},
		watch:
		{
			fromPreset(newValue)
			{
				if (newValue !== "__custom__")
					this.model.from = newValue;
			},
			serializedQueryString()
			{
				this.schedulePreview(600);
			},
			localPreviewServer()
			{
				this.schedulePreview(1);
			}
		}
	}
</script>

<style scoped>
	.graphEditorRoot
	{
		display: flex;
		flex-direction: column;
		width: calc(100vw - 30px);
		height: calc(100vh - 30px);
		box-sizing: border-box;
		padding: 10px 14px;
	}

	.title
	{
		border-bottom: 1px solid #000000;
		margin-bottom: 8px;
		font-size: 20px;
		flex: 0 0 auto;
	}

	.editorBody
	{
		flex: 1 1 auto;
		display: flex;
		gap: 16px;
		min-height: 0px;
	}

	.controlsPane
	{
		flex: 0 0 470px;
		overflow-y: auto;
		padding-right: 6px;
		min-height: 0px;
	}

	.previewPane
	{
		flex: 1 1 auto;
		display: flex;
		flex-direction: column;
		min-width: 0px;
		min-height: 0px;
	}

	.previewHeader
	{
		flex: 0 0 auto;
		display: flex;
		align-items: center;
		gap: 12px;
		margin-bottom: 6px;
	}

	.previewTitle
	{
		font-weight: bold;
	}

	.previewServerLabel
	{
		font-size: 13px;
	}

	.previewImageWrapper
	{
		flex: 1 1 auto;
		position: relative;
		border: 1px solid #CCCCCC;
		background-color: #FDFDFD;
		min-height: 0px;
		overflow: hidden;
	}

	.previewImage
	{
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	.previewLoading
	{
		position: absolute;
		top: 8px;
		right: 8px;
	}

	.previewErrorBox, .previewEmptyBox
	{
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		text-align: center;
		color: #555555;
		background-color: rgba(255,255,255,0.9);
		padding: 12px 18px;
		border-radius: 6px;
	}

	.previewErrorBox
	{
		color: #AA0000;
		border: 1px solid #AA0000;
	}

	.queryStringBox
	{
		flex: 0 0 auto;
		margin-top: 8px;
	}

	.queryStringHead
	{
		display: flex;
		justify-content: space-between;
		align-items: center;
		font-weight: bold;
		font-size: 13px;
		margin-bottom: 3px;
	}

	.queryStringText
	{
		width: 100%;
		box-sizing: border-box;
		font-family: Consolas, monospace;
		font-size: 11px;
		color: #333333;
		resize: vertical;
	}

	.fieldBlock
	{
		margin-bottom: 14px;
	}

	.fieldLabel
	{
		font-weight: bold;
		margin-bottom: 3px;
		display: block;
	}

	.sectionHead
	{
		font-size: 15px;
		border-bottom: 1px solid #DDDDDD;
		padding-bottom: 2px;
	}

	.labelNote
	{
		font-weight: normal;
		color: #666666;
		font-size: 12px;
	}

	.fieldHelp
	{
		font-size: 12px;
		color: #555555;
		margin-top: 3px;
	}

	.wideInput
	{
		width: 100%;
		box-sizing: border-box;
		font-size: 14px;
		padding: 3px 5px;
	}

	.timeRangeRow
	{
		display: flex;
		gap: 6px;
	}

	.advancedTime
	{
		margin-top: 4px;
		font-size: 13px;
	}

		.advancedTime summary
		{
			cursor: pointer;
			color: #1e78af;
			font-size: 12px;
		}

	.targetBox
	{
		border: 1px solid #B5B5B5;
		border-radius: 4px;
		margin-bottom: 6px;
		background-color: #FFFFFF;
	}

	.targetHeader
	{
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 4px 6px;
		background-color: #EFEFEF;
		border-radius: 4px;
	}

	.targetToggle
	{
		flex: 0 0 auto;
		cursor: pointer;
		font-weight: bold;
		white-space: nowrap;
	}

	.targetSummary
	{
		flex: 1 1 auto;
		font-family: Consolas, monospace;
		font-size: 11px;
		color: #555555;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		min-width: 0px;
	}

	.targetError
	{
		color: #CC0000;
		font-weight: bold;
	}

	.targetButtons
	{
		flex: 0 0 auto;
	}

		.targetButtons input[type=button]
		{
			margin-left: 3px;
		}

	.targetBody
	{
		padding: 8px;
		border-top: 1px solid #DDDDDD;
	}

	.addSeriesRow
	{
		margin-top: 4px;
	}

	.optionRow
	{
		display: flex;
		align-items: center;
		gap: 6px;
		margin: 4px 0px;
	}

	.optionLabel
	{
		flex: 0 0 160px;
		font-size: 13px;
	}

	.optionRow input[type=text], .optionRow input[type=number], .optionRow select
	{
		flex: 1 1 auto;
		font-size: 13px;
		padding: 2px 4px;
		min-width: 0px;
	}

	.helpToggle
	{
		flex: 0 0 auto;
		display: inline-block;
		width: 18px;
		height: 18px;
		line-height: 18px;
		text-align: center;
		border-radius: 50%;
		border: 1px solid #1e78af;
		color: #1e78af;
		cursor: pointer;
		font-size: 12px;
		user-select: none;
	}

	.optionHelpBox
	{
		font-size: 12px;
		color: #333333;
		background-color: #F0F6FA;
		border: 1px solid #C9DEEC;
		border-radius: 4px;
		padding: 6px 8px;
		margin-top: 6px;
	}

	.otherParamRow
	{
		display: flex;
		align-items: center;
		gap: 4px;
		margin: 3px 0px;
	}

	.otherParamKey
	{
		flex: 0 0 160px;
		font-family: Consolas, monospace;
		font-size: 12px;
	}

	.otherParamValue
	{
		flex: 1 1 auto;
		font-family: Consolas, monospace;
		font-size: 12px;
		min-width: 0px;
	}

	.buttonRow
	{
		flex: 0 0 auto;
		text-align: right;
		border-top: 1px solid #AAAAAA;
		padding-top: 8px;
		margin-top: 8px;
	}

		.buttonRow input[type=button]
		{
			margin-left: 8px;
			font-size: 14px;
			padding: 4px 14px;
		}

	.primaryButton
	{
		font-weight: bold;
	}
</style>
